import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { LearningCard } from "Components/LearningCard/LearningCard";
import { LearningCardSkeleton } from "Components/LearningCardSkeleton/LearningCardSkeleton";
import { CourseTrackingCard } from "Components/CourseTrackingCard/CourseTrackingCard";
import { CourseTrackingCardSkeleton } from "Components/CourseTrackingCardSkeleton/CourseTrackingCardSkeleton";
import { Survey } from "Components/Survey/Survey";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { useSessionStorage } from "Hooks/useSessionStorage";
import { useQuery } from "Hooks/useQuery";
import { useMutation } from "Hooks/useMutation";
import { useNotificationTimer } from "Hooks/useNotificationTimer";
import { useProgressCalculation } from "Hooks/useProgressCalculation.jsx";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import {
  getPaqueteDetalle,
  postProgresoPaquete,
  asignPaquete,
} from "Services/paquete_cursos_service";
import { getContenidoCurso, getEncuestaCurso } from "Services/cursos_service";
import {
  crearActa,
  actualizarActa,
  crearActaCliente,
} from "Services/actas_service.jsx";
import {
  ULTIMO_PAQUETE,
  ULTIMO_PAQUETE_ESTRUCTURA,
  ULTIMO_CURSO_ESTRUCTURA,
  ESTADOS_ACTA,
  TIPOS_CLIENTE,
  SEGMENTOS,
} from "Constantes/constants";
import { RUTAS } from "rutas";
import { postRespuestaEncuesta } from "Services/encuesta_service";
import "./CursoPaquete.scss";

export const CursoPaquete = () => {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState(0);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { idPaquete, idCurso } = useParams();
  const { authUser: user } = useSelector((state) => state.auth);
  const { value: ultimoPaquete, setValue: setUltimoPaquete } =
    useSessionStorage(ULTIMO_PAQUETE);
  const { triggerNotificationFetch } = useNotificationTimer();
  const { refetch: refetchCursosPaquetes } = useCursosPaquetes();

  const {
    data: paquete,
    isLoading,
    refetch: refetchPaquete,
  } = useQuery(getPaqueteDetalle, {
    idUsuario: user?.idUsuario,
    idPaquete: idPaquete,
  });

  const cursoData =
    paquete?.cursos?.find((c) => c.idCurso === parseInt(idCurso)) || null;

  const { calculateMaterialsPercentage, calculateTotalPercentage } =
    useProgressCalculation(cursoData);

  const { mutate: mutateAsignPaquete } = useMutation(asignPaquete);
  const { mutate: mutateCrearActa } = useMutation(crearActa);
  const { mutate: mutateActualizarActa } = useMutation(actualizarActa);
  const { mutate: mutateCrearActaCliente } = useMutation(crearActaCliente);
  const { mutate: mutatePostRespuestaEncuesta } = useMutation(
    postRespuestaEncuesta
  );

  const {
    data: contenidoData,
    isLoading: isLoadingContenido,
    refetch: refetchContenido,
  } = useQuery(getContenidoCurso, {
    idUsuario: user?.idUsuario,
    idCurso: idCurso,
    idPaquete: idPaquete,
    formatoExcluir: ".encuesta",
  });

  const contentSteps = React.useMemo(() => {
    if (!contenidoData) return [];

    const parents = contenidoData.filter(
      (item) => item.idContenidoPadre === null
    );

    return parents.map((parent) => {
      const children = contenidoData.filter(
        (item) => item.idContenidoPadre === parent.idContenidoCurso
      );

      const stepItems = [parent, ...children];

      return {
        stepTitle: parent.titulo,
        contentItems: stepItems,
      };
    });
  }, [contenidoData]);

  const { data: encuestaData } = useQuery(
    getEncuestaCurso,
    {
      idEncuesta: cursoData?.encuesta?.idEncuesta,
    },
    Boolean(cursoData?.encuesta?.idEncuesta)
  );

  const crearActaAnonima = async () => {
    if (Boolean(user?.idUsuario)) return;

    await mutateCrearActa({
      idCurso: parseInt(idCurso),
      idEstadoActa: ESTADOS_ACTA.FINALIZADA.ID,
      idSegmento: SEGMENTOS.CAPACITACION_ANONIMA.ID,
      idPaqueteCursos: idPaquete,
    });
  };

  const handleCloseMaterial = async (materialData) => {
    if (window.screen.orientation.type.startsWith("portrait")) return;

    if (Boolean(user?.idUsuario)) {
      if (materialData.completado) return;

      setIsLoadingProgress(true);
      await handleAuthenticatedProgress(materialData);
      setIsLoadingProgress(false);
    } else {
      handleUnauthenticatedProgress(materialData);
    }
  };

  const handleAuthenticatedProgress = async (materialData) => {
    const progressData = {
      idCurso: parseInt(idCurso),
      idPaqueteCursos: parseInt(idPaquete),
      idUsuario: user.idUsuario,
      idContenidoCurso: materialData.idContenidoCurso,
      usuarioModificacion: user.usuario,
    };

    await postProgresoPaquete(progressData);
    await refetchContenido();
    const paqueteActualizado = await refetchPaquete();
    await handleActa(paqueteActualizado);

    if (parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete)) {
      updateLocalStorageProgress(materialData);
    }
  };

  const handleActa = async (paqueteActualizado) => {
    setIsLoadingProgress(false);
    const paquete = paqueteActualizado || { cursos: [cursoData] };
    console.log(paquete);
    const curso =
      paquete?.cursos?.find((c) => c.idCurso === parseInt(idCurso)) ||
      cursoData;
    let idActa = curso?.idActa;

    if (!idActa) {
      const idActaNueva = await mutateCrearActa({
        idUsuario: user.idUsuario,
        idCurso: parseInt(idCurso),
        idEstadoActa: ESTADOS_ACTA.INICIADA.ID,
        usuario: user.usuario,
        idSegmento: user.segmento,
        idPaqueteCursos: paquete?.idPaquete
      });
      idActa = idActaNueva;
    }

    if (!idActa) return;

    const completeProgress = curso?.progreso?.porcentajeTotal || 0;
    if (completeProgress > 0 && completeProgress < 100) {
      await mutateActualizarActa({
        idActa,
        idUsuario: user.idUsuario,
        idCurso: parseInt(idCurso),
        idEstadoActa: ESTADOS_ACTA.EN_CURSO.CODIGO,
        usuario: user.usuario,
      });
    }

    if (completeProgress === 100) {
      await finalizarActaCompleta(idActa);
    }
  };

  const finalizarActaCompleta = async (idActa) => {
    const actaActualizada = await mutateActualizarActa({
      idActa,
      idUsuario: user.idUsuario,
      idCurso: parseInt(idCurso),
      idEstadoActa: ESTADOS_ACTA.FINALIZADA.CODIGO,
      usuario: user.usuario,
    });

    if (actaActualizada) {
      await mutateCrearActaCliente({
        idTipoCliente: TIPOS_CLIENTE.AUTONOMO.ID,
        idActa,
        fud: "",
        dpi: user.usuario,
        usuario: user.usuario,
      });
      triggerNotificationFetch();
      refetchCursosPaquetes();
    }
  };

  const handleUnauthenticatedProgress = (materialData) => {
    if (parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete)) {
      updateLocalStorageProgress(materialData);
    } else {
      const nuevoCurso = {
        ...ULTIMO_CURSO_ESTRUCTURA,
        idCurso: parseInt(idCurso),
        materiales:
          contenidoData?.map((material) => ({
            idContenidoCurso: material.idContenidoCurso,
            completado:
              material.idContenidoCurso === materialData.idContenidoCurso,
          })) || [],
        encuesta: {
          idEncuesta: cursoData?.encuesta?.idEncuesta || null,
          idContenidoCurso: cursoData?.encuesta?.idContenidoCurso || null,
          completada: false,
          respuestas: [],
        },
      };

      nuevoCurso.progreso.porcentajeMateriales = calculateMaterialsPercentage(
        nuevoCurso.materiales
      );
      nuevoCurso.progreso.porcentajeTotal = calculateTotalPercentage(
        nuevoCurso.materiales,
        nuevoCurso.encuesta
      );

      const progresoData = {
        ...ULTIMO_PAQUETE_ESTRUCTURA,
        idPaquete: parseInt(idPaquete),
        cursos: [nuevoCurso],
      };
      setUltimoPaquete(progresoData);

      if (nuevoCurso.progreso.porcentajeTotal === 100) {
        crearActaAnonima();
      }
    }
  };

  const updateLocalStorageProgress = (materialData) => {
    const cursosActuales = ultimoPaquete?.cursos || [];
    const cursoExistente = cursosActuales.find(
      (c) => c.idCurso === parseInt(idCurso)
    );

    const allCourseMaterials =
      contenidoData?.map((material) => {
        const existingMaterial = cursoExistente?.materiales?.find(
          (m) => m.idContenidoCurso === material.idContenidoCurso
        );
        const isCurrentMaterial =
          material.idContenidoCurso === materialData.idContenidoCurso;

        return {
          idContenidoCurso: material.idContenidoCurso,
          completado:
            isCurrentMaterial || existingMaterial?.completado || false,
        };
      }) || [];

    const encuesta = cursoExistente?.encuesta || {
      idEncuesta: cursoData?.encuesta?.idEncuesta || null,
      idContenidoCurso: cursoData?.encuesta?.idContenidoCurso || null,
      completada: false,
      respuestas: [],
    };

    const cursoActualizado = {
      ...ULTIMO_CURSO_ESTRUCTURA,
      idCurso: parseInt(idCurso),
      materiales: allCourseMaterials,
      encuesta: encuesta,
      progreso: {
        porcentajeMateriales: calculateMaterialsPercentage(allCourseMaterials),
        porcentajeTotal: calculateTotalPercentage(allCourseMaterials, encuesta),
      },
    };

    let cursosNuevos;
    if (cursoExistente) {
      cursosNuevos = cursosActuales.map((curso) =>
        curso.idCurso === parseInt(idCurso) ? cursoActualizado : curso
      );
    } else {
      cursosNuevos = [...cursosActuales, cursoActualizado];
    }

    setUltimoPaquete({ ...ultimoPaquete, cursos: cursosNuevos });

    if (cursoActualizado.progreso.porcentajeTotal === 100) {
      crearActaAnonima();
    }
  };

  const getStepProgress = (stepItems) => {
    const isAuthenticated = Boolean(user?.idUsuario);

    if (isAuthenticated) {
      const mainContentCompletedAt = stepItems?.[0]?.fechaCompletado;
      const courseProgressCompletedAt = cursoData?.progreso?.fechaCompletado;

      if (
        mainContentCompletedAt &&
        courseProgressCompletedAt &&
        mainContentCompletedAt <= courseProgressCompletedAt
      ) {
        return 100;
      }

      const completedItems = stepItems.filter((item) => item.completado).length;
      const totalItems = stepItems.length;
      return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    }

    if (parseInt(ultimoPaquete?.idPaquete) !== parseInt(idPaquete)) return 0;

    const cursoEnProgreso = ultimoPaquete?.cursos?.find(
      (c) => c.idCurso === parseInt(idCurso)
    );
    if (!cursoEnProgreso) return 0;

    const completedItems = stepItems.filter((item) => {
      const material = cursoEnProgreso.materiales?.find(
        (m) => m.idContenidoCurso === item.idContenidoCurso
      );
      return material?.completado || false;
    }).length;

    const totalItems = stepItems.length;
    return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  };

  const getSurveyCompletedStatus = () => {
    if (Boolean(user?.idUsuario)) {
      return cursoData?.encuesta?.encuestaCompletada;
    } else {
      const cursoEnProgreso =
        parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete) &&
        ultimoPaquete?.cursos?.find((c) => c.idCurso === parseInt(idCurso));
      return cursoEnProgreso?.encuesta?.completada;
    }
  };

  const isEncuestaDisabled = () => {
    const isAuthenticated = Boolean(user?.idUsuario);
    const isPaqueteActive =
      parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete);
    const cursoEnProgreso = ultimoPaquete?.cursos?.find(
      (c) => c.idCurso === parseInt(idCurso)
    );

    if (isAuthenticated) {
      return cursoData?.progreso?.porcentajeMateriales !== 100;
    }

    if (!isPaqueteActive || !cursoEnProgreso) return true;

    return cursoEnProgreso?.progreso?.porcentajeMateriales !== 100;
  };

  const handleCompleteSurvey = async (answers) => {
    const transformedAnswers = Object.entries(answers).map(
      ([idPregunta, answer]) => ({
        idActa: cursoData?.idActa,
        idUsuario: user?.idUsuario,
        idEncuesta: cursoData?.encuesta?.idEncuesta,
        idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
        idPregunta: parseInt(idPregunta),
        idRespuesta: answer.selectedOption,
        idSegmento: SEGMENTOS.CAPACITACION_AUTONOMA.ID,
        usuario: user?.usuario,
      })
    );

    const cursoEnProgreso =
      parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete) &&
      ultimoPaquete?.cursos?.find((c) => c.idCurso === parseInt(idCurso));

    const materialesExistentes = cursoEnProgreso?.materiales || [];
    const encuestaActualizada = {
      idEncuesta: cursoData?.encuesta?.idEncuesta,
      idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
      completada: true,
      respuestas: transformedAnswers,
    };

    const cursoActualizado = {
      ...ULTIMO_CURSO_ESTRUCTURA,
      idCurso: parseInt(idCurso),
      materiales: materialesExistentes,
      encuesta: encuestaActualizada,
      progreso: {
        porcentajeMateriales:
          calculateMaterialsPercentage(materialesExistentes),
        porcentajeTotal: calculateTotalPercentage(
          materialesExistentes,
          encuestaActualizada
        ),
      },
    };

    const cursosActuales = ultimoPaquete?.cursos || [];
    const cursosActualizados = cursoEnProgreso
      ? cursosActuales.map((c) =>
          c.idCurso === parseInt(idCurso) ? cursoActualizado : c
        )
      : [...cursosActuales, cursoActualizado];

    setUltimoPaquete({
      ...ultimoPaquete,
      idPaquete: parseInt(idPaquete),
      cursos: cursosActualizados,
    });

    if (Boolean(user?.idUsuario)) {
      setIsLoadingProgress(true);
      await handleAuthenticatedProgress({
        idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
      });
      setIsLoadingProgress(false);

      Promise.all(
        transformedAnswers.map((answer) => mutatePostRespuestaEncuesta(answer))
      );
    }

    if (cursoActualizado.progreso.porcentajeTotal === 100) {
      await crearActaAnonima();
    }

    triggerNotificationFetch();
    refetchCursosPaquetes();
    navigate(RUTAS.PAQUETE(idPaquete));
  };

  const handleBackButton = () => {
    setActiveCard(activeCard - 1);
  };

  const handleActionButton = () => {
    const lastContentStepIndex = contentSteps.length - 1;
    const isOnLastContentStep = activeCard === lastContentStepIndex;
    const courseCompleted = cursoData?.progreso?.porcentajeTotal === 100;

    if (!isOnLastContentStep) {
      setActiveCard(activeCard + 1);
      return;
    }

    if (courseCompleted) {
      navigate(RUTAS.PAQUETE(idPaquete));
      return;
    }

    const hasSurvey = Boolean(cursoData?.encuesta?.idEncuesta);
    if (hasSurvey) {
      setActiveCard(contentSteps.length);
      return;
    }

    navigate(RUTAS.PAQUETE(idPaquete));
  };

  const getIsLastContent = () => {
    if (!hasLoaded) return false;

    const lastContentStepIndex = contentSteps.length - 1;
    const hasSurvey = Boolean(cursoData?.encuesta?.idEncuesta);
    const courseCompleted = cursoData?.progreso?.porcentajeTotal === 100;

    if (activeCard !== lastContentStepIndex) return false;

    if (courseCompleted) return true;

    return !hasSurvey;
  };

  const getActionButtonDisabled = () => {
    const isAuthenticated = Boolean(user?.idUsuario);
    const lastContentStepIndex = contentSteps.length - 1;

    if (isLoadingProgress || isLoading || isLoadingContenido) return true;

    if (activeCard !== lastContentStepIndex) return false;

    if (isAuthenticated) {
      return cursoData?.progreso?.porcentajeMateriales !== 100;
    }

    const isPaqueteActive =
      parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete);
    const cursoEnProgreso = ultimoPaquete?.cursos?.find(
      (c) => c.idCurso === parseInt(idCurso)
    );

    if (!isPaqueteActive || !cursoEnProgreso) return true;
    return cursoEnProgreso?.progreso?.porcentajeMateriales !== 100;
  };

  useEffect(() => {
    if (paquete === null) {
      navigate(RUTAS.PRINCIPAL);
    }
  }, [paquete, navigate]);

  useEffect(() => {
    if (paquete && contenidoData && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [paquete, contenidoData, hasLoaded]);

  useEffect(() => {
    if (paquete && cursoData === null) {
      navigate(RUTAS.PAQUETE(idPaquete));
    }
  }, [paquete, cursoData, navigate, idPaquete]);

  useEffect(
    () => {
      if (Boolean(user?.idUsuario) && paquete && !paquete?.asignado) {
        const asignarPaquete = async () => {
          await mutateAsignPaquete({
            idUsuario: user.idUsuario,
            idPaqueteCursos: parseInt(idPaquete),
          });
          triggerNotificationFetch();
          refetchCursosPaquetes();
        };
        asignarPaquete();
      }
    },
    [user?.idUsuario, paquete, idPaquete] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const isPaqueteActive =
      parseInt(ultimoPaquete?.idPaquete) === parseInt(idPaquete);
    const cursoLocal = ultimoPaquete?.cursos?.find(
      (c) => c.idCurso === parseInt(idCurso)
    );

    if (
      !isPaqueteActive ||
      !cursoLocal ||
      !contenidoData ||
      Boolean(user?.idUsuario)
    )
      return;

    const activeIds = contenidoData.map((m) => m.idContenidoCurso);
    const existingMaterials = cursoLocal?.materiales?.filter((m) =>
      activeIds.includes(m.idContenidoCurso)
    );

    const wasCompleted = cursoLocal.progreso?.porcentajeTotal === 100;
    const savedIds = cursoLocal?.materiales?.map((m) => m.idContenidoCurso);
    const hasNewIds = activeIds.some((id) => !savedIds.includes(id));
    const hasNewMaterials = wasCompleted && hasNewIds;

    const newSurveyAdded =
      wasCompleted &&
      !cursoLocal.encuesta?.idEncuesta &&
      cursoData?.encuesta?.idEncuesta;

    const needsUpdate =
      existingMaterials.length !== cursoLocal?.materiales?.length ||
      hasNewMaterials ||
      newSurveyAdded;

    if (needsUpdate) {
      const allMaterials = hasNewMaterials
        ? activeIds.map(
            (id) =>
              cursoLocal?.materiales?.find(
                (m) => m.idContenidoCurso === id
              ) || {
                idContenidoCurso: id,
                completado: false,
              }
          )
        : existingMaterials;

      const encuesta = newSurveyAdded
        ? {
            idEncuesta: cursoData?.encuesta?.idEncuesta,
            idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
            completada: false,
            respuestas: [],
          }
        : cursoLocal?.encuesta;

      const cursoActualizado = {
        ...cursoLocal,
        materiales: allMaterials,
        encuesta,
        progreso: {
          porcentajeMateriales: calculateMaterialsPercentage(allMaterials),
          porcentajeTotal: calculateTotalPercentage(allMaterials, encuesta),
        },
      };

      const cursosActualizados = ultimoPaquete?.cursos?.map((c) =>
        c.idCurso === parseInt(idCurso) ? cursoActualizado : c
      );

      setUltimoPaquete({
        ...ultimoPaquete,
        cursos: cursosActualizados,
      });
    }
  }, [contenidoData, user?.idUsuario]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="curso-container">
        <section className="curso-container__breadcrumb">
          <Breadcrumbs aria-label="breadcrumb" className="text-gris-acero">
            <Link
              className="breadcrumb__default"
              onClick={() => {
                navigate(RUTAS.PRINCIPAL);
              }}
            >
              Inicio
            </Link>
            <Link
              className="breadcrumb__highlighted"
              onClick={() => {
                navigate(RUTAS.PAQUETE(idPaquete));
              }}
            >
              {!hasLoaded ? "Cargando..." : paquete?.titulo}
            </Link>
            <Link className="breadcrumb__static">
              {!hasLoaded ? "Cargando..." : cursoData?.titulo}
            </Link>
          </Breadcrumbs>
        </section>
        <section
          className={clsx("curso-container__sidebar", {
            "curso-container__sidebar--hidden":
              hasLoaded && contentSteps.length === 0,
          })}
        >
          {!hasLoaded && <CourseTrackingCardSkeleton />}

          {hasLoaded &&
            contentSteps.map((step, index) => (
              <CourseTrackingCard
                key={`step-${index}`}
                title={step.stepTitle}
                completed={getStepProgress(step.contentItems) === 100}
                active={activeCard === index}
                onClick={() => setActiveCard(index)}
              />
            ))}

          {!hasLoaded && <CourseTrackingCardSkeleton />}
          {hasLoaded &&
            Boolean(cursoData?.encuesta?.idEncuesta) &&
            (cursoData?.progreso?.porcentajeTotal !== 100 ||
              (isLoadingProgress && activeCard === contentSteps.length)) && (
              <CourseTrackingCard
                variant="survey"
                completed={getSurveyCompletedStatus()}
                active={activeCard === contentSteps.length}
                onClick={() => setActiveCard(contentSteps.length)}
                disabled={isEncuestaDisabled()}
              />
            )}
        </section>
        <section
          className={clsx("curso-container__content", {
            "curso-container__content--hidden":
              hasLoaded && contentSteps.length === 0,
          })}
        >
          {!hasLoaded && <LearningCardSkeleton />}

          {hasLoaded &&
            contentSteps.map(
              (step, index) =>
                activeCard === index && (
                  <LearningCard
                    key={`learning-card-${index}`}
                    titulo={step.stepTitle}
                    categoria={cursoData?.categoria}
                    progress={getStepProgress(step.contentItems)}
                    isLoadingProgress={isLoadingProgress || isLoadingContenido}
                    contentItems={step.contentItems}
                    onCloseMaterial={handleCloseMaterial}
                    isAuthenticated={!!user?.idUsuario}
                    onBack={handleBackButton}
                    onAction={handleActionButton}
                    isLastContent={getIsLastContent()}
                    actionButtonDisabled={getActionButtonDisabled()}
                    backButtonDisabled={activeCard === 0}
                    isLoading={
                      isLoadingProgress || isLoading || isLoadingContenido
                    }
                  />
                )
            )}

          {hasLoaded && activeCard === contentSteps.length && (
            <Survey
              completed={getSurveyCompletedStatus()}
              onCompleteSurvey={handleCompleteSurvey}
              surveyData={encuestaData}
              isLoading={isLoadingProgress}
            />
          )}
        </section>
        {hasLoaded && contentSteps.length === 0 && (
          <div className="curso-container__empty-state">
            <InformativeText>
              <NotebookIcon />
              Este curso no tiene contenido disponible en este momento.
            </InformativeText>
          </div>
        )}
      </div>
      <Footer />
    </Layout>
  );
};
